import React from 'react';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { useAuthStore } from '../stores/authStore';
import { RootStackParamList } from './types';
import AuthNavigator from './AuthNavigator';
import MainTabNavigator from './MainTabNavigator';
import PaymentResultScreen from '../screens/billing/PaymentResultScreen';
import VietQRScreen from '../screens/billing/VietQRScreen';
import EmailVerificationScreen from '../screens/auth/EmailVerificationScreen';
import AuthCallbackScreen from '../screens/auth/AuthCallbackScreen';
import SubscriptionScreen from '../screens/billing/SubscriptionScreen';
import MembershipManagementScreen from '../screens/billing/MembershipManagementScreen';
import PickupPersonsScreen from '../screens/profile/PickupPersonsScreen';
import LegalGateScreen from '../screens/legal/LegalGateScreen';
import SubUserBlockedScreen from '../screens/legal/SubUserBlockedScreen';
import PublicLegalDocScreen from '../screens/legal/PublicLegalDocScreen';
import AccountDeletionScreen from '../screens/auth/AccountDeletionScreen';

const Stack = createNativeStackNavigator<RootStackParamList>();

export default function RootNavigator({ initialRouteName }: { initialRouteName?: keyof RootStackParamList }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const needsOnboarding = useAuthStore((s) => s.needsOnboarding);
  const needsSubscription = useAuthStore((s) => s.needsSubscription);
  const emailVerified = useAuthStore((s) => s.emailVerified);
  const pendingVerificationPaymentId = useAuthStore((s) => s.pendingVerificationPaymentId);
  const hasPausedMembership = useAuthStore((s) => s.hasPausedMembership);
  const needsLegalAgreements = useAuthStore((s) => s.needsLegalAgreements);
  const subUserBlocked = useAuthStore((s) => s.subUserBlocked);

  // User is fully authenticated when verified, onboarded, and either subscribed OR pending verification OR paused,
  // AND all legal agreements are signed (and not blocked as a sub-user)
  // pendingVerificationPaymentId bypasses both needsSubscription and needsLegalAgreements gates
  const isFullyAuthenticated = isAuthenticated && emailVerified && !needsOnboarding
    && (!needsSubscription || !!pendingVerificationPaymentId || hasPausedMembership)
    && (!needsLegalAgreements || !!pendingVerificationPaymentId) && !subUserBlocked;

  const user = useAuthStore((s) => s.user);

  return (
    <Stack.Navigator screenOptions={{ headerShown: false }} initialRouteName={initialRouteName}>
      {isFullyAuthenticated ? (
        <>
          <Stack.Screen name="Main" component={MainTabNavigator} />
          <Stack.Screen
            name="PaymentResult"
            component={PaymentResultScreen}
            options={{ gestureEnabled: false, animation: 'fade' }}
          />
          <Stack.Screen
            name="VietQR"
            component={VietQRScreen}
            options={{ gestureEnabled: false }}
          />
          <Stack.Screen
            name="MembershipManagement"
            component={MembershipManagementScreen}
            options={{ gestureEnabled: true }}
          />
          <Stack.Screen
            name="PickupPersons"
            component={PickupPersonsScreen}
            options={{ gestureEnabled: true }}
          />
        </>
      ) : isAuthenticated && !emailVerified ? (
        // Authenticated but email not verified — force verification screen
        <Stack.Screen
          name="EmailVerification"
          component={EmailVerificationScreen}
          initialParams={{ email: user?.email || '' }}
        />
      ) : isAuthenticated && emailVerified && needsOnboarding ? (
        // Authenticated and verified but needs onboarding (birthday + terms)
        <Stack.Screen name="AuthCallback" component={AuthCallbackScreen} />
      ) : isAuthenticated && emailVerified && !needsOnboarding && needsSubscription ? (
        // Authenticated and onboarded but needs subscription
        <>
          <Stack.Screen name="Subscription" component={SubscriptionScreen} options={{ gestureEnabled: false }} />
          <Stack.Screen
            name="VietQR"
            component={VietQRScreen}
            options={{ gestureEnabled: false }}
          />
        </>
      ) : isAuthenticated && emailVerified && !needsOnboarding && !needsSubscription && subUserBlocked ? (
        // Authenticated but blocked because guardian hasn't signed waiver
        <Stack.Screen name="SubUserBlocked" component={SubUserBlockedScreen} options={{ gestureEnabled: false }} />
      ) : isAuthenticated && emailVerified && !needsOnboarding && !needsSubscription && needsLegalAgreements ? (
        // Authenticated but needs to sign legal documents
        <Stack.Screen name="LegalGate" component={LegalGateScreen} options={{ gestureEnabled: false }} />
      ) : (
        <Stack.Screen name="Auth" component={AuthNavigator} />
      )}
      {/* Always-available screens, reachable in every auth state (e.g. via deep link) */}
      <Stack.Screen name="PublicLegalDoc" component={PublicLegalDocScreen} options={{ presentation: 'modal' }} />
      <Stack.Screen
        name="AccountDeletion"
        component={AccountDeletionScreen}
        options={{ gestureEnabled: true, headerShown: true, headerTitle: 'Account Deletion' }}
      />
    </Stack.Navigator>
  );
}
